import { EPOCH } from './constants';

/**
 * Which daily grid it is, in the player's own timezone — a new grid at local
 * midnight, everywhere.
 */
export function dayNumber(now: Date = new Date()): number {
  // Take the local calendar date, then measure it on the UTC timeline: local
  // days are 23 or 25 hours long across a daylight-saving change, which would
  // otherwise repeat or skip a grid number once a year.
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const epoch = Date.UTC(EPOCH.year, EPOCH.month, EPOCH.day);
  return Math.round((today - epoch) / 86400000) + 1;
}

/** The calendar date a given daily grid belongs to. */
export function dateForDay(day: number): Date {
  return new Date(EPOCH.year, EPOCH.month, EPOCH.day + (day - 1));
}

/** Short, locale-aware label for a grid's date — "Aug 16". */
export function formatDay(day: number, locale?: string): string {
  return dateForDay(day).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** Milliseconds until the next grid deals — the coming local midnight. */
export function msUntilNextGrid(now: Date = new Date()): number {
  // Built from calendar fields, so a 23- or 25-hour daylight-saving day still
  // ends exactly when the clock shows midnight.
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return midnight.getTime() - now.getTime();
}

/** What an hour and a minute are called in the language being spoken. */
export interface WaitUnits {
  hours: string;
  minutes: string;
}

/**
 * "9h 41m" / "41m" — the wait, rounded up so it never reads "0m".
 *
 * The unit letters are copy, so the caller passes them in; the default keeps
 * this readable on its own and is what the English dictionary supplies.
 */
export function formatWait(ms: number, units: WaitUnits = { hours: 'h', minutes: 'm' }): string {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m === 0) return `${h}${units.hours}`;
  return h > 0 ? `${h}${units.hours} ${m}${units.minutes}` : `${m}${units.minutes}`;
}
